import React, { useState, useEffect } from 'react';
import {
  Box, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, Typography, Alert, MenuItem, Select, FormControl,
  InputLabel, Chip, IconButton, TablePagination, FormHelperText
} from '@mui/material';
import { LockReset, Edit as EditIcon, Delete as DeleteIcon, AttachMoney, Visibility } from '@mui/icons-material';
import { userService, clientService, roleService } from '../services/api';
import EmployeeDetailDrilldown from './EmployeeDetailDrilldown';
import { usePagination } from '../hooks/usePagination';
import { EMPLOYMENT_TYPE_META } from './internshipTypes';

const EmployeeManagement: React.FC = () => {
  const [users, setUsers] = useState<any[]>([]);
  const [clients, setClients] = useState<any[]>([]);
  const [roles, setRoles] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  // Defaults to Active so deactivated employees don't clutter the list by default — admins can
  // still switch to Inactive/All when they specifically need to see them (e.g. to reactivate).
  const [statusFilter, setStatusFilter] = useState<'active' | 'inactive' | 'all'>('active');
  const [open, setOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [credentialsDialog, setCredentialsDialog] = useState(false);
  const [resetPasswordDialog, setResetPasswordDialog] = useState(false);
  const [salaryDialog, setSalaryDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [viewUser, setViewUser] = useState<any>(null);
  const [salaryAmount, setSalaryAmount] = useState('');
  const [generatedCredentials, setGeneratedCredentials] = useState<any>(null);
  const [message, setMessage] = useState('');
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    role: 'Developer',
    clientId: '',
    managerId: '',
    username: '',
    password: '',
    employmentType: ''
  });
  const [employmentFilter, setEmploymentFilter] = useState('');

  useEffect(() => {
    loadUsers();
    loadClients();
    loadRoles();
  }, []);

  const loadUsers = async () => {
    try {
      const data = await userService.getUsers();
      setUsers(data);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const loadClients = async () => {
    try {
      const data = await clientService.getClients();
      setClients(data);
    } catch (error) {
      console.error('Error loading clients:', error);
    }
  };

  const loadRoles = async () => {
    try {
      const data = await roleService.getRoles();
      setRoles(data || []);
    } catch (error) {
      console.error('Error loading roles:', error);
      setRoles([{ id: 1, name: 'Admin' }, { id: 2, name: 'Manager' }, { id: 3, name: 'Employee' }]);
    }
  };

  const handleCreate = async () => {
    try {
      const payload: any = {
        fullName: formData.fullName,
        email: formData.email,
        role: formData.role,
        clientId: formData.clientId ? parseInt(formData.clientId) : undefined,
        employmentType: formData.employmentType || undefined,
      };
      
      if (formData.username) {
        payload.username = formData.username;
      }
      
      if (formData.password) {
        payload.password = formData.password;
      }
      
      const result = await userService.createUser(payload);
      
      // Assign role with manager if specified
      if (formData.managerId && formData.role) {
        const roleToAssign = roles.find(r => r.name === formData.role);
        if (roleToAssign) {
          try {
            await roleService.assignRole(
              result.id,
              roleToAssign.id,
              parseInt(formData.managerId)
            );
          } catch (error) {
            console.error('Error assigning role:', error);
          }
        }
      }
      
      setGeneratedCredentials(result);
      setCredentialsDialog(true);
      setMessage('Employee created successfully');
      setOpen(false);
      loadUsers();
      setFormData({ fullName: '', email: '', role: 'Developer', clientId: '', managerId: '', username: '', password: '', employmentType: '' });
      setTimeout(() => setMessage(''), 3000);
    } catch (error: any) {
      setMessage(error.response?.data?.message || 'Error creating employee');
    }
  };

  const handleEditClick = (user: any) => {
    setSelectedUser(user);
    setFormData({
      fullName: user.fullName,
      email: user.email,
      role: user.role,
      clientId: user.clientId ? user.clientId.toString() : '',
      managerId: '',
      username: '',
      password: '',
      employmentType: user.employmentType || ''
    });
    setEditOpen(true);
  };

  const handleUpdate = async () => {
    if (!selectedUser) return;
    try {
      const payload: any = {
        fullName: formData.fullName,
        email: formData.email,
        role: formData.role,
        clientId: formData.clientId ? parseInt(formData.clientId) : 0,
        // Only sent when changed — the API refuses relabelling someone mid-internship.
        employmentType: formData.employmentType && formData.employmentType !== selectedUser.employmentType ? formData.employmentType : undefined
      };
      
      await userService.updateUser(selectedUser.id, payload);
      
      // Update role assignment with manager if specified
      if (formData.managerId && formData.role) {
        const roleToAssign = roles.find(r => r.name === formData.role);
        if (roleToAssign) {
          try {
            await roleService.assignRole(
              selectedUser.id,
              roleToAssign.id,
              parseInt(formData.managerId)
            );
          } catch (error) {
            console.error('Error updating role assignment:', error);
          }
        }
      }
      
      setMessage('Employee updated successfully');
      setEditOpen(false);
      loadUsers();
      setSelectedUser(null);
      setTimeout(() => setMessage(''), 3000);
    } catch (error: any) {
      setMessage(error.response?.data?.message || 'Error updating employee');
    }
  };

  const handleDeleteClick = (user: any) => {
    setSelectedUser(user);
    setDeleteOpen(true);
  };

  const handleDelete = async () => {
    if (!selectedUser) return;
    try {
      await userService.deleteUser(selectedUser.id);
      setMessage('Employee deleted successfully');
      setDeleteOpen(false);
      loadUsers();
      setSelectedUser(null);
      setTimeout(() => setMessage(''), 3000);
    } catch (error: any) {
      setMessage(error.response?.data?.message || 'Error deleting employee');
    }
  };

  const handleResetPassword = async () => {
    if (!selectedUser) return;
    
    try {
      const result = await userService.resetPassword(selectedUser.id);
      setGeneratedCredentials(result);
      setResetPasswordDialog(false);
      setCredentialsDialog(true);
      setMessage('Password reset successfully');
      setTimeout(() => setMessage(''), 3000);
    } catch (error: any) {
      setMessage(error.response?.data?.message || 'Error resetting password');
    }
  };

  const openResetPasswordDialog = (user: any) => {
    setSelectedUser(user);
    setResetPasswordDialog(true);
  };

  const openSalaryDialog = (user: any) => {
    setSelectedUser(user);
    setSalaryAmount(user.salary?.toString() || '');
    setSalaryDialog(true);
  };

  const handleUpdateSalary = async () => {
    if (!selectedUser) return;
    
    try {
      await userService.updateSalary(selectedUser.id, parseFloat(salaryAmount));
      setMessage('Salary updated successfully');
      setSalaryDialog(false);
      loadUsers();
      setTimeout(() => setMessage(''), 3000);
    } catch (error: any) {
      setMessage(error.response?.data?.message || 'Error updating salary');
    }
  };

  const filteredUsers = users.filter(user => {
    if (statusFilter === 'active' && !user.isActive) return false;
    if (statusFilter === 'inactive' && user.isActive) return false;
    if (employmentFilter === 'none' && user.employmentType) return false;
    if (employmentFilter && employmentFilter !== 'none' && user.employmentType !== employmentFilter) return false;

    const search = searchTerm.toLowerCase();
    return (
      user.username?.toLowerCase().includes(search) ||
      user.fullName?.toLowerCase().includes(search) ||
      user.email?.toLowerCase().includes(search)
    );
  });
  const { pagedRows: pagedUsers, paginationProps: usersPaginationProps } = usePagination(filteredUsers);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h6">Employees</Typography>
        <Button variant="contained" onClick={() => setOpen(true)}>
          Create Employee
        </Button>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <TextField
          label="Search employees by name, username, or email"
          variant="outlined"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ flex: '1 1 320px' }}
        />
        <FormControl sx={{ minWidth: 160 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={(e) => setStatusFilter(e.target.value as 'active' | 'inactive' | 'all')}
          >
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
            <MenuItem value="all">All</MenuItem>
          </Select>
        </FormControl>
        <FormControl sx={{ minWidth: 180 }}>
          <InputLabel>Employment Type</InputLabel>
          <Select value={employmentFilter} label="Employment Type" onChange={(e) => setEmploymentFilter(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            {Object.entries(EMPLOYMENT_TYPE_META).map(([value, meta]) => (
              <MenuItem key={value} value={value}>{meta.label}</MenuItem>
            ))}
            <MenuItem value="none">Not set</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {message && <Alert severity="info" sx={{ mb: 2 }}>{message}</Alert>}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Username</TableCell>
              <TableCell>Full Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Employment Type</TableCell>
              <TableCell>Client</TableCell>
              <TableCell>Salary</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pagedUsers.map((user) => (
              <TableRow key={user.id}>
                <TableCell>{user.username}</TableCell>
                <TableCell>{user.fullName}</TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>
                  <Chip label={user.role} color={user.role === 'Admin' ? 'primary' : 'default'} size="small" />
                </TableCell>
                <TableCell>
                  {user.employmentType
                    ? <Chip label={EMPLOYMENT_TYPE_META[user.employmentType]?.label ?? user.employmentType} color={EMPLOYMENT_TYPE_META[user.employmentType]?.color ?? 'default'} size="small" variant="outlined" />
                    : '-'}
                </TableCell>
                <TableCell>{user.clientName || '-'}</TableCell>
                <TableCell>{user.salary ? `₹${user.salary.toLocaleString()}` : '-'}</TableCell>
                <TableCell>
                  <Chip label={user.isActive ? 'Active' : 'Inactive'} color={user.isActive ? 'success' : 'default'} size="small" />
                </TableCell>
                <TableCell>
                  <IconButton
                    size="small"
                    color="info"
                    onClick={() => setViewUser(user)}
                    title="View Details"
                  >
                    <Visibility />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => handleEditClick(user)}
                    title="Edit"
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDeleteClick(user)}
                    title="Delete"
                  >
                    <DeleteIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="warning"
                    onClick={() => openResetPasswordDialog(user)}
                    title="Reset Password"
                  >
                    <LockReset />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="success"
                    onClick={() => openSalaryDialog(user)}
                    title="Update Salary"
                  >
                    <AttachMoney />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {filteredUsers.length > 0 && <TablePagination {...usersPaginationProps} />}
      </TableContainer>

      {/* Create Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Employee</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Full Name"
            value={formData.fullName}
            onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            margin="normal"
            required
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Role</InputLabel>
            <Select
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              label="Role"
            >
              {roles.map((role) => (
                <MenuItem key={role.id} value={role.name}>
                  {role.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel>Employment Type</InputLabel>
            <Select
              value={formData.employmentType}
              onChange={(e) => setFormData({ ...formData, employmentType: e.target.value })}
              label="Employment Type"
            >
              {Object.entries(EMPLOYMENT_TYPE_META).map(([value, meta]) => (
                <MenuItem key={value} value={value}>{meta.label}</MenuItem>
              ))}
            </Select>
            <FormHelperText>
              Role controls access; employment type is the kind of engagement. Intern: create the internship from the Internships screen (or it is created automatically from onboarding).
            </FormHelperText>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel>Client (Optional)</InputLabel>
            <Select
              value={formData.clientId}
              onChange={(e) => setFormData({ ...formData, clientId: e.target.value })}
              label="Client (Optional)"
            >
              <MenuItem value="">None</MenuItem>
              {clients.map((client) => (
                <MenuItem key={client.id} value={client.id}>
                  {client.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel>Manager (Optional)</InputLabel>
            <Select
              value={formData.managerId}
              onChange={(e) => setFormData({ ...formData, managerId: e.target.value })}
              label="Manager (Optional)"
            >
              <MenuItem value="">No Manager</MenuItem>
              {users.filter(u => u.role === 'Manager' || u.role === 'Admin').map((manager) => (
                <MenuItem key={manager.id} value={manager.id}>
                  {manager.fullName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Alert severity="info" sx={{ mt: 2, mb: 1 }}>
            Leave username and password empty to auto-generate credentials
          </Alert>
          <TextField
            fullWidth
            label="Username (Optional - Auto-generated)"
            value={formData.username}
            onChange={(e) => setFormData({ ...formData, username: e.target.value })}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Password (Optional - Auto-generated)"
            type="password"
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleCreate} variant="contained">Create</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Employee</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Username"
            value={selectedUser?.username || ''}
            margin="normal"
            disabled
            helperText="Username cannot be changed"
          />
          <TextField
            fullWidth
            label="Full Name"
            value={formData.fullName}
            onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            margin="normal"
            required
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Role</InputLabel>
            <Select
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              label="Role"
            >
              {roles.map((role) => (
                <MenuItem key={role.id} value={role.name}>
                  {role.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel>Employment Type</InputLabel>
            <Select
              value={formData.employmentType}
              onChange={(e) => setFormData({ ...formData, employmentType: e.target.value })}
              label="Employment Type"
            >
              {Object.entries(EMPLOYMENT_TYPE_META).map(([value, meta]) => (
                <MenuItem key={value} value={value}>{meta.label}</MenuItem>
              ))}
            </Select>
            <FormHelperText>
              Role controls access; employment type is the kind of engagement. Intern: create the internship from the Internships screen (or it is created automatically from onboarding).
            </FormHelperText>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel>Client (Optional)</InputLabel>
            <Select
              value={formData.clientId}
              onChange={(e) => setFormData({ ...formData, clientId: e.target.value })}
              label="Client (Optional)"
            >
              <MenuItem value="">None</MenuItem>
              {clients.map((client) => (
                <MenuItem key={client.id} value={client.id}>
                  {client.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel>Manager (Optional)</InputLabel>
            <Select
              value={formData.managerId}
              onChange={(e) => setFormData({ ...formData, managerId: e.target.value })}
              label="Manager (Optional)"
            >
              <MenuItem value="">No Manager</MenuItem>
              {users.filter(u => u.role === 'Manager' || u.role === 'Admin').map((manager) => (
                <MenuItem key={manager.id} value={manager.id}>
                  {manager.fullName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Alert severity="info" sx={{ mt: 2 }}>
            Use Reset Password button to change the employee's password.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button onClick={handleUpdate} variant="contained">Update</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <DialogTitle>Delete Employee</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{selectedUser?.fullName}</strong>?
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            This will deactivate the employee account. They will no longer be able to log in.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} variant="contained" color="error">Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetPasswordDialog} onClose={() => setResetPasswordDialog(false)}>
        <DialogTitle>Reset Password</DialogTitle>
        <DialogContent>
          <Typography sx={{ mt: 2 }}>
            Are you sure you want to reset the password for <strong>{selectedUser?.fullName}</strong>?
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            A new password will be auto-generated and displayed once.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetPasswordDialog(false)}>Cancel</Button>
          <Button onClick={handleResetPassword} variant="contained" color="warning">
            Reset Password
          </Button>
        </DialogActions>
      </Dialog>

      {/* Salary Update Dialog */}
      <Dialog open={salaryDialog} onClose={() => setSalaryDialog(false)}>
        <DialogTitle>Update Salary</DialogTitle>
        <DialogContent>
          <Typography sx={{ mt: 2, mb: 2 }}>
            Update monthly salary for <strong>{selectedUser?.fullName}</strong>
          </Typography>
          <TextField
            fullWidth
            label="Monthly Salary"
            type="number"
            value={salaryAmount}
            onChange={(e) => setSalaryAmount(e.target.value)}
            margin="normal"
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSalaryDialog(false)}>Cancel</Button>
          <Button onClick={handleUpdateSalary} variant="contained" color="success">
            Update Salary
          </Button>
        </DialogActions>
      </Dialog>

      {/* Credentials Display Dialog */}
      <Dialog open={credentialsDialog} onClose={() => setCredentialsDialog(false)}>
        <DialogTitle>Credentials Generated</DialogTitle>
        <DialogContent>
          <Alert severity="success" sx={{ mb: 2 }}>
            Please save these credentials. They will only be shown once.
          </Alert>
          {generatedCredentials && (
            <Box>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Full Name:</strong> {generatedCredentials.fullName}
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Email:</strong> {generatedCredentials.email}
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Username:</strong> {generatedCredentials.username}
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Password:</strong> {generatedCredentials.password}
              </Typography>
              {generatedCredentials.clientName && (
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <strong>Client:</strong> {generatedCredentials.clientName}
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCredentialsDialog(false)} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <EmployeeDetailDrilldown user={viewUser} onClose={() => setViewUser(null)} onDocumentVerified={loadUsers} />
    </Box>
  );
};

export default EmployeeManagement;
